import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { LandingRow, LandingsPayload } from "@dashboard/shared";
import { ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { toQueryParams, type DateRangeValue } from "@/components/DateRangePicker";
import { SourceIcon } from "@/components/icons";
import { cn, fmtInt } from "@/lib/utils";

// ---- Kachel "Seite" (Operator 2026-10-10): wie verhalten sich Anzeigen-Besucher auf der Landingpage?
// Je Kampagne (Google/YouTube, Meta, Microsoft) Besuche, Verweildauer, Mobil-Anteil und Anteil der Besuche mit Klick auf
// einen Partner-Knopf. Quelle: Tracker /stats/landings ueber /api/landings. Aufbau wie die Kapital-Karte daneben:
// zugeklappt eine Zeile mit den Summen, aufgeklappt die Liste. Alles Weitere (Anfragen, Mittelwert, Scrolltiefe, Geraete,
// eToro-Klicks) steht nur im Tooltip (Operator: so wenig Text wie moeglich).
// Verweildauer = MEDIAN: der Mittelwert wird von offen gelassenen Tabs verzerrt (z. B. Ø 573 s bei Median 40 s).
// Bei ~200 Kampagnen (fast alle Microsoft) je Quelle eine Gruppe mit Zwischensumme, hoechstens 15 Zeilen, Rest aufklappbar;
// Filter-Chips oben zeigen nur eine Quelle. ----

const OFFEN_KEY = "brokers:seite-offen";
const QUELLE_KEY = "brokers:seite-quelle";
const QUELLE: Record<string, string> = { google: "Google / YouTube", meta: "Meta", microsoft: "Microsoft" };
/** Feste Reihenfolge der Gruppen: die Video-Tests (Google/YouTube, Meta) vor dem grossen Microsoft-Block. */
const REIHENFOLGE = ["google", "meta", "microsoft"];
const SICHTBAR = 15;

/** Zahlenspalten schmal und fest, der Name nimmt den Rest und kuerzt mit "…" - keine Luecken zwischen den Zahlen. */
const RASTER = "grid items-center gap-x-2 [grid-template-columns:minmax(0,1fr)_3rem_2.75rem_2.75rem_3.5rem]";

function lesen(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function schreiben(key: string, wert: string): void {
  try { localStorage.setItem(key, wert); } catch { /* privates Fenster o. ae. */ }
}

/** Verweildauer kurz: "31 s", ab einer Minute "2:05". */
export function fmtDauer(s: number | null | undefined): string {
  if (s == null || !Number.isFinite(s)) return "–";
  const r = Math.round(s);
  return r < 60 ? `${r} s` : `${Math.floor(r / 60)}:${String(r % 60).padStart(2, "0")}`;
}

const prozent = (x: number | null | undefined) => (x == null || !Number.isFinite(x) ? "–" : `${Math.round(x * 100)} %`);
const name = (r: LandingRow) => r.campaign || r.campaign_id || "?";
const quelleName = (q: string) => QUELLE[q] ?? q;

type Summe = { kampagnen: number; edge: number; loaded: number; desktop: number; mobile: number; tablet: number; cta: number; etoro: number;
  median: number | null; mittel: number | null };

/** Summen mehrerer Kampagnen. Verweildauer: der echte Median ueber alle Besuche ist hier nicht bekannt - Naeherung ist der
 *  nach Besuchen gewichtete Median der Kampagnen-Mediane (bzw. das gewichtete Mittel der Mittelwerte fuer den Hover). */
function summieren(rows: LandingRow[]): Summe {
  const s: Summe = { kampagnen: rows.length, edge: 0, loaded: 0, desktop: 0, mobile: 0, tablet: 0, cta: 0, etoro: 0, median: null, mittel: null };
  for (const r of rows) {
    s.edge += r.edge; s.loaded += r.loaded; s.desktop += r.desktop; s.mobile += r.mobile; s.tablet += r.tablet;
    s.cta += r.cta_visits; s.etoro += r.etoro_clicks;
  }
  const med = rows.filter((r) => r.median_dwell_s != null && r.loaded > 0).sort((a, b) => (a.median_dwell_s as number) - (b.median_dwell_s as number));
  const nMed = med.reduce((x, r) => x + r.loaded, 0);
  let kum = 0;
  for (const r of med) { kum += r.loaded; if (kum * 2 >= nMed) { s.median = r.median_dwell_s; break; } }
  const mit = rows.filter((r) => r.avg_dwell_s != null && r.loaded > 0);
  const nMit = mit.reduce((x, r) => x + r.loaded, 0);
  s.mittel = nMit ? mit.reduce((x, r) => x + (r.avg_dwell_s as number) * r.loaded, 0) / nMit : null;
  return s;
}

const anteil = (teil: number, ganz: number) => (ganz ? prozent(teil / ganz) : "–");

function summenTitel(titel: string, s: Summe): string {
  return [
    titel,
    `Anfragen ${fmtInt(s.edge)} · Besuche ${fmtInt(s.loaded)}`,
    `Desktop ${fmtInt(s.desktop)} · Mobil ${fmtInt(s.mobile)} · Tablet ${fmtInt(s.tablet)}`,
    `Verweil Median ≈ ${fmtDauer(s.median)} · Ø ${fmtDauer(s.mittel)}`,
    `Knopf-Klicks ${fmtInt(s.cta)} (${anteil(s.cta, s.loaded)}) · eToro-Klicks ${fmtInt(s.etoro)}`,
  ].join("\n");
}

function zeilenTitel(r: LandingRow): string {
  return [
    name(r),
    `${quelleName(r.source)}${r.campaign_id ? ` · ${r.campaign_id}` : ""}`,
    `Anfragen ${fmtInt(r.edge)} · Besuche ${fmtInt(r.loaded)}`,
    `Desktop ${fmtInt(r.desktop)} · Mobil ${fmtInt(r.mobile)} · Tablet ${fmtInt(r.tablet)}`,
    `Verweil Median ${fmtDauer(r.median_dwell_s)} · Ø ${fmtDauer(r.avg_dwell_s)} · Scroll ${r.avg_scroll_pct != null ? `${Math.round(r.avg_scroll_pct)} %` : "–"}`,
    `Knopf-Klicks ${fmtInt(r.cta_visits)} · eToro-Klicks ${fmtInt(r.etoro_clicks)}`,
  ].join("\n");
}

/** Vier Zahlen einer Zeile (Kampagne oder Gruppensumme). */
function Zahlen({ loaded, median, mobile, rate }: { loaded: number; median: number | null; mobile: number; rate: number | null }) {
  const leer = (t: string) => cn("text-right", t === "–" && "text-muted-foreground");
  const v = fmtDauer(median), m = loaded ? prozent(mobile / loaded) : "–", k = prozent(rate);
  return (
    <>
      <div className={cn("text-right", loaded === 0 && "text-muted-foreground")}>{fmtInt(loaded)}</div>
      <div className={leer(v)}>{v}</div>
      <div className={leer(m)}>{m}</div>
      <div className={leer(k)}>{k}</div>
    </>
  );
}

export function SeiteKarte({ dateRange }: { dateRange: DateRangeValue }) {
  const [offen, setOffen] = useState(() => lesen(OFFEN_KEY) === "1");
  // Filter: "alle" oder eine Quelle (bleibt ueber Neuladen erhalten).
  const [filter, setFilter] = useState(() => lesen(QUELLE_KEY) ?? "alle");
  // Gruppen mit allen Zeilen statt der ersten 15 (nur fuer diesen Besuch).
  const [ganz, setGanz] = useState<Set<string>>(() => new Set());
  const [daten, setDaten] = useState<LandingsPayload | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const zeitraum = JSON.stringify(toQueryParams(dateRange));

  useEffect(() => {
    const params = JSON.parse(zeitraum) as ReturnType<typeof toQueryParams>;
    let cancelled = false;
    setDaten(null); setFehler(null);
    const laden = () => api.landings(params)
      .then((r) => { if (!cancelled) { setDaten(r); setFehler(null); } })
      .catch((e: Error) => { if (!cancelled) setFehler(e.message); });
    laden();
    // Jede Minute nachladen - die Route hat keinen Cache, die Zahlen sollen live sein.
    const id = setInterval(laden, 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [zeitraum]);

  // Meta 16.10.-22.10. ausgeblendet, seit 22.10. wieder sichtbar (Funnel-Kampagnen); Reddit seit 16.10. abends wieder an.
  const zeilen = useMemo(() => (daten?.rows ?? [])
    .filter((r) => r.loaded > 0 || r.edge > 0)
    .sort((a, b) => b.loaded - a.loaded || b.edge - a.edge || name(a).localeCompare(name(b))), [daten]);

  const gruppen = useMemo(() => {
    const quellen = [...new Set(zeilen.map((r) => r.source))]
      .sort((a, b) => (REIHENFOLGE.indexOf(a) + 1 || 99) - (REIHENFOLGE.indexOf(b) + 1 || 99) || a.localeCompare(b));
    return quellen.map((q) => {
      const rows = zeilen.filter((r) => r.source === q);
      return { quelle: q, rows, summe: summieren(rows) };
    });
  }, [zeilen]);

  const gesamt = useMemo(() => summieren(zeilen), [zeilen]);
  // Gespeicherter Filter auf eine Quelle, die im Zeitraum fehlt: dann alle zeigen.
  const aktiv = gruppen.some((g) => g.quelle === filter) ? filter : "alle";
  const sichtbareGruppen = aktiv === "alle" ? gruppen : gruppen.filter((g) => g.quelle === aktiv);

  const laedt = daten === null && !fehler;
  const umschalten = () => setOffen((o) => { schreiben(OFFEN_KEY, o ? "0" : "1"); return !o; });
  const waehle = (q: string) => { setFilter(q); schreiben(QUELLE_KEY, q); };
  const ganzUmschalten = (q: string) => setGanz((prev) => {
    const next = new Set(prev);
    if (next.has(q)) next.delete(q); else next.add(q);
    return next;
  });

  const chip = (key: string, inhalt: ReactNode, titel: string) => (
    <button key={key} type="button" onClick={() => waehle(key)} aria-pressed={aktiv === key} title={titel} data-seite-filter={key}
      className={cn("inline-flex h-6 items-center gap-1 rounded-md border px-1.5 text-[11.5px] tabular-nums transition-colors",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        aktiv === key ? "border-primary/40 bg-primary/5 text-foreground" : "border-border text-muted-foreground hover:border-primary/40")}>
      {inhalt}
    </button>
  );

  return (
    <section className="overflow-hidden rounded-xl border border-border bg-card" data-section="Seite">
      <button type="button" onClick={umschalten} aria-expanded={offen} aria-controls="seite-panel" data-tile="seite"
        title={fehler ? `Tracker nicht erreichbar: ${fehler}` : daten ? summenTitel("Alle Kampagnen", gesamt) : undefined}
        className="flex w-full min-w-0 items-center gap-2 px-5 py-2.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <h3 className="text-[17px] tracking-tight">Seite</h3>
        <span className="ml-1.5 flex min-w-0 items-baseline gap-2.5 truncate text-[12px] tabular-nums text-muted-foreground" data-seite="kurz">
          <span>Besuche <span className="text-foreground">{laedt ? "…" : fehler ? "–" : fmtInt(gesamt.loaded)}</span></span>
          <span>Verweil <span className="text-foreground">{laedt ? "…" : fehler ? "–" : fmtDauer(gesamt.median)}</span></span>
        </span>
        <ChevronDown className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform", offen && "rotate-180")} />
      </button>

      {offen && (
        <div id="seite-panel" className="border-t border-border px-5 pb-2.5 pt-2" data-seite-panel>
          {fehler ? (
            <div className="py-1 text-[12px] text-destructive" title={fehler}>Tracker nicht erreichbar</div>
          ) : laedt ? (
            <div className="py-1 text-[12px] text-muted-foreground">Lädt …</div>
          ) : zeilen.length === 0 ? (
            <div className="py-1 text-[12px] text-muted-foreground">Keine Besuche im Zeitraum</div>
          ) : (
            <>
              {/* Filter nur, wenn es mehr als eine Quelle gibt. Zahl = Kampagnen. */}
              {gruppen.length > 1 && (
                <div className="mb-1.5 flex flex-wrap items-center gap-1" data-seite-filterleiste>
                  {chip("alle", <>Alle <span className="opacity-70">{fmtInt(zeilen.length)}</span></>, `${fmtInt(zeilen.length)} Kampagnen`)}
                  {gruppen.map((g) => chip(g.quelle,
                    <><SourceIcon source={g.quelle} className="h-3 w-3" /><span className="opacity-70">{fmtInt(g.rows.length)}</span></>,
                    `${quelleName(g.quelle)}: ${fmtInt(g.rows.length)} Kampagnen · ${fmtInt(g.summe.loaded)} Besuche`))}
                </div>
              )}
              <div className="text-[13px] tabular-nums">
                <div className={cn(RASTER, "h-6 whitespace-nowrap text-[11.5px] text-muted-foreground")}>
                  <div>Kampagne</div>
                  <div className="text-right" title="Seite im Browser geladen">Besuche</div>
                  <div className="text-right" title="Median der Verweildauer (Ø im Hover)">Verweil</div>
                  <div className="text-right" title="Anteil Smartphone an den Besuchen">Mobil</div>
                  <div className="text-right" title="Anteil der Besuche mit Klick auf einen Partner-Knopf">Klickrate</div>
                </div>
                {sichtbareGruppen.map((g) => {
                  const alle = ganz.has(g.quelle);
                  const rows = alle ? g.rows : g.rows.slice(0, SICHTBAR);
                  const rest = g.rows.length - SICHTBAR;
                  return (
                    <div key={g.quelle} data-seite-gruppe={g.quelle}>
                      {/* Gruppenkopf = Zwischensumme der Quelle. */}
                      <div className={cn(RASTER, "-mx-2 h-8 cursor-default rounded-md bg-muted/50 px-2")} title={summenTitel(quelleName(g.quelle), g.summe)}>
                        <div className="flex min-w-0 items-center gap-1.5">
                          <SourceIcon source={g.quelle} className="h-4 w-4 shrink-0" />
                          <span className="truncate">{quelleName(g.quelle)}</span>
                          <span className="shrink-0 text-[11.5px] text-muted-foreground">{fmtInt(g.rows.length)}</span>
                        </div>
                        <Zahlen loaded={g.summe.loaded} median={g.summe.median} mobile={g.summe.mobile}
                          rate={g.summe.loaded ? g.summe.cta / g.summe.loaded : null} />
                      </div>
                      {rows.map((r) => (
                        <div key={`${r.campaign_id}:${r.campaign ?? ""}`} title={zeilenTitel(r)} data-seite-zeile={r.campaign_id}
                          className={cn(RASTER, "h-7 cursor-default border-t border-border/60")}>
                          <div className="truncate">{name(r)}</div>
                          <Zahlen loaded={r.loaded} median={r.median_dwell_s} mobile={r.mobile} rate={r.cta_rate} />
                        </div>
                      ))}
                      {rest > 0 && (
                        <button type="button" onClick={() => ganzUmschalten(g.quelle)} aria-expanded={alle} data-seite-mehr={g.quelle}
                          className="mb-1 h-7 w-full border-t border-border/60 text-left text-[12px] text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                          {alle ? "weniger" : `+${fmtInt(rest)} weitere`}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}
